import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest, loginRequest } from '../config/msalConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});

  const isLoading = inProgress !== 'none';

  const login = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(() => {
    try {
      instance.logoutPopup();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      // Try to get token silently first
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });
      
      return response.accessToken;
    } catch (error) {
      // If silent token acquisition fails, try interactive popup
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenPopup({
            ...tokenRequest,
            account,
          });
          return response.accessToken;
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError);
          return null;
        }
      } else {
        console.error('Silent token acquisition failed:', error);
        return null;
      }
    }
  }, [instance, account]);

  const value: AuthContextType = {
    isAuthenticated,
    user: account,
    login,
    logout,
    getAccessToken,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};